import { observer } from 'mobx-react-lite';
import { motion } from 'framer-motion';
import { ChevronDown, User, Loader2, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { mapStatus } from '@/lib/clientStatus';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { addRecentClient } from '@/lib/recentClients';
import { currencySign } from '@/lib/currency';
import { countryStore } from '@/stores/country.store';
import type { AccountSearch } from '@/models/clients/types';
import type { AccountType } from '@/models/account/types';

const rowVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring' as const, stiffness: 380, damping: 28 },
  },
};

const filterLabels: Record<string, string> = {
  all: 'Все статусы',
  active: 'Активные',
  debtor: 'Должники',
  inactive: 'Неактивные',
};

interface StoreSlice {
  filtered: AccountSearch[];
  isLoading: boolean;
  error: string | null;
  statusFilter: string;
  accountTypeFilter: string;
  selectOpen: boolean;
  typeSelectOpen: boolean;
  accountTypes: AccountType[];
}

interface Props {
  store: StoreSlice;
  setStatusFilter: (v: string) => void;
  setAccountTypeFilter: (v: string) => void;
  setSelectOpen: (value: boolean | ((prev: boolean) => boolean)) => void;
  setTypeSelectOpen: (v: boolean | ((p: boolean) => boolean)) => void;
  onClientClick: (client: AccountSearch) => void;
}

export const ClientsSearchResults = observer(function ClientsSearchResults({
  store,
  setStatusFilter,
  setAccountTypeFilter,
  setSelectOpen,
  setTypeSelectOpen,
  onClientClick,
}: Props) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, delay: 0.15, ease: 'easeOut' }}
      className='w-full mt-3'
    >
      <div className='flex items-center justify-between mb-3 gap-3 flex-wrap'>
        <p className='text-sm text-muted-foreground'>
          {store.isLoading ? 'Поиск...' : `Найдено: ${store.filtered.length}`}
        </p>
        <div className='flex gap-2'>
          {store.accountTypes.length > 0 && (
            <div className='relative'>
              <Button
                variant='ghost'
                onClick={() => setTypeSelectOpen((prev) => !prev)}
                className='gap-2 px-3 py-1.5 h-auto rounded-xl bg-card border border-border/50 text-sm text-foreground min-w-36 justify-between'
              >
                {store.accountTypeFilter === 'all' ? 'Все типы' : store.accountTypeFilter}
                <ChevronDown
                  className={cn(
                    'w-4 h-4 text-muted-foreground transition-transform',
                    store.typeSelectOpen && 'rotate-180'
                  )}
                />
              </Button>
              {store.typeSelectOpen && (
                <div className='absolute top-full mt-1 right-0 w-full bg-card border border-border/50 rounded-xl overflow-hidden z-10 shadow-lg min-w-max'>
                  <button
                    onClick={() => {
                      setAccountTypeFilter('all');
                      setTypeSelectOpen(false);
                    }}
                    className={cn(
                      'w-full text-left px-4 py-2.5 text-sm hover:bg-muted/50 transition-colors',
                      store.accountTypeFilter === 'all' && 'text-primary'
                    )}
                  >
                    Все типы
                  </button>
                  {store.accountTypes.map((accountType) => (
                    <button
                      key={accountType.id_account_type}
                      onClick={() => {
                        setAccountTypeFilter(accountType.account_type_name);
                        setTypeSelectOpen(false);
                      }}
                      className={cn(
                        'w-full text-left px-4 py-2.5 text-sm hover:bg-muted/50 transition-colors',
                        store.accountTypeFilter === accountType.account_type_name && 'text-primary'
                      )}
                    >
                      {accountType.account_type_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className='relative'>
            <Button
              variant='ghost'
              onClick={() => setSelectOpen((prev) => !prev)}
              className='gap-2 px-3 py-1.5 h-auto rounded-xl bg-card border border-border/50 text-sm text-foreground min-w-40 justify-between'
            >
              {filterLabels[store.statusFilter]}
              <ChevronDown
                className={cn(
                  'w-4 h-4 text-muted-foreground transition-transform',
                  store.selectOpen && 'rotate-180'
                )}
              />
            </Button>
            {store.selectOpen && (
              <div className='absolute top-full mt-1 right-0 w-full bg-card border border-border/50 rounded-xl overflow-hidden z-10 shadow-lg'>
                {Object.entries(filterLabels).map(([value, label]) => (
                  <button
                    key={value}
                    onClick={() => {
                      setStatusFilter(value);
                      setSelectOpen(false);
                    }}
                    className={cn(
                      'w-full text-left px-4 py-2.5 text-sm hover:bg-muted/50 transition-colors',
                      store.statusFilter === value && 'text-primary'
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='border border-border/30 rounded-2xl bg-card overflow-hidden'>
        {store.error ? (
          <div className='flex items-center justify-center gap-2 py-16 text-destructive text-sm'>
            <AlertTriangle className='w-4 h-4' />
            {store.error}
          </div>
        ) : store.isLoading ? (
          <div className='flex items-center justify-center py-16'>
            <Loader2 className='w-6 h-6 animate-spin text-muted-foreground' />
          </div>
        ) : store.filtered.length === 0 ? (
          <div className='py-16 text-center text-muted-foreground text-sm'>Клиенты не найдены</div>
        ) : (
          <>
            <div className='grid grid-cols-12 gap-4 px-5 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider border-b border-border/30'>
              <div className='col-span-4'>Клиент</div>
              <div className='col-span-3'>Логин</div>
              <div className='col-span-2'>Статус услуг</div>
              <div className='col-span-2'>Статус</div>
              <div className='col-span-1 text-right'>Баланс</div>
            </div>
            <motion.div
              className='divide-y divide-border/20'
              variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
              initial='hidden'
              animate='visible'
            >
              {store.filtered.map((client) => {
                const status = mapStatus(client.status_name);
                const balance = parseFloat(client.balance) || 0;
                const currency = currencySign(countryStore.byName.get(client.country_name));
                return (
                  <motion.a
                    key={client.id_account}
                    variants={rowVariants}
                    href={`/clients/${client.id_account}`}
                    onClick={(e) => {
                      if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) {
                        e.preventDefault();
                        onClientClick(client);
                      } else {
                        addRecentClient(client);
                      }
                    }}
                    className='grid grid-cols-12 gap-4 px-5 py-4 cursor-pointer hover:bg-muted/30 transition-colors items-center'
                  >
                    <div className='col-span-4'>
                      <p className='text-sm font-medium text-foreground'>{client.account_name}</p>
                      {client.account_type_name && (
                        <p className='text-xs text-muted-foreground mt-0.5'>
                          {client.account_type_name}
                        </p>
                      )}
                    </div>
                    <div className='col-span-3 flex items-center gap-2'>
                      <User className='w-3.5 h-3.5 text-muted-foreground shrink-0' />
                      <span className='text-xs font-mono text-muted-foreground truncate'>
                        {client.login}
                      </span>
                    </div>
                    <div className='col-span-2'>
                      <span className='text-xs text-muted-foreground'>
                        {client.services_status || '—'}
                      </span>
                    </div>
                    <div className='col-span-2'>
                      <StatusBadge
                        status={status}
                        label={client.status_name || undefined}
                        size='md'
                      />
                    </div>
                    <div className='col-span-1 text-right'>
                      <span
                        className={cn(
                          'text-sm font-semibold',
                          balance < 0
                            ? 'text-red-400'
                            : balance > 0
                              ? 'text-green-400'
                              : 'text-muted-foreground'
                        )}
                      >
                        {balance > 0 ? '+' : ''}
                        {balance.toLocaleString('ru-RU')} {currency}
                      </span>
                    </div>
                  </motion.a>
                );
              })}
            </motion.div>
          </>
        )}
      </div>
    </motion.div>
  );
});
